// import node module libraries
import React, { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { Col, Row, Container, Form, Card } from 'react-bootstrap';

// Import Loading Animation
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'

// import translation hook
import { useTranslation } from 'react-i18next';

// import hooks
import useToggle from 'hooks/useToggle';

// import sub components
import PlanPricingCard from './PlanPricingCard';

const PlanPricingTable = ({ title, description, plans, isLoading }) => {
    const { t } = useTranslation(); // useTranslation-Hook
    const [billingInterval, setBillingInterval] = useState('year');

    // Filter plans based on billing interval
    const getFilteredPlans = () => {
        if (!plans || plans.length === 0) return [];

        // Group plans by product_name
        const groupedPlans = plans.reduce((acc, plan) => {
            if (!acc[plan.product_name]) {
                acc[plan.product_name] = {};
            }
            acc[plan.product_name][plan.interval] = plan;
            return acc;
        }, {});

        // Return plans for selected interval, fallback to month if year not available
        const filteredPlans = Object.values(groupedPlans).map(productPlans => {
            return productPlans[billingInterval] || productPlans['month'];
        }).filter(Boolean);

        // Sort plans by amount (price) in ascending order
        return filteredPlans.sort((a, b) => a.amount - b.amount);
    };

    return (
        <Fragment>
            <section className="py-lg-13 py-8 bg-primary">
                <Container>
                    {/* Page header */}
                    <Row className="align-items-center">
                        <Col xl={{ span: 8, offset: 2 }} lg={12} md={12} sm={12}>
                            <div className="text-center mb-6 px-md-8">
                                <h1 className="text-white display-3 fw-bold">{t(title)}</h1>
                                <p className="text-white lead mb-4">{t(description)}</p>
                                {/* Old Switch Toggle
                                <div
                                    id="pricing-switch"
                                    className="d-flex justify-content-center align-items-center"
                                >
                                    <span className="text-white me-2">{t('Monthly')}</span>
                                    <Form>
                                        <Form.Check
                                            name="radios"
                                            type="checkbox"
                                            className="form-switch"
                                            id="pricingSwitch"
                                            checked={billingInterval === 'year'}
                                            onChange={(e) => setBillingInterval(e.target.checked ? 'year' : 'month')}
                                        />
                                    </Form>
                                    <span className="text-white ms-2">{t('Yearly')}</span>
                                </div> */}
                                {/* Billing Toggle */}
                                <div className="text-center mb-6">
                                    <div className="d-inline-flex align-items-center p-1 bg-light rounded-pill">
                                        <span className={`px-3 py-2 rounded-pill fw-medium ${billingInterval === 'month' ? 'bg-white text-primary shadow-sm' : 'text-muted'}`}>
                                            {t('Monthly')}
                                        </span>
                                        <Form.Check
                                            type="switch"
                                            id="billing-switch"
                                            className="mx-2"
                                            checked={billingInterval === 'year'}
                                            onChange={(e) => setBillingInterval(e.target.checked ? 'year' : 'month')}
                                        />
                                        <span className={`px-3 py-2 rounded-pill fw-medium ${billingInterval === 'year' ? 'bg-white text-primary shadow-sm' : 'text-muted'}`}>
                                            {t('Yearly')}
                                            {(() => {
                                                // Berechne die maximale prozentuale Ersparnis zwischen Monat und Jahr
                                                const filteredPlans = getFilteredPlans();
                                                let percentSaved = null;
                                                if (filteredPlans.length > 0) {
                                                    const plan = filteredPlans.reduce((max, p) => (p.amount > (max?.amount ?? 0) ? p : max), null); // Finde den Plan mit dem höchsten Betrag
                                                    const allPlans = plans.filter(p => p.product_name === plan.product_name);
                                                    const monthly = allPlans.find(p => p.interval === 'month');
                                                    const yearly = allPlans.find(p => p.interval === 'year');
                                                    if (monthly && yearly) {
                                                        const monthlyTotal = monthly.amount * 12;
                                                        const yearlyTotal = yearly.amount;
                                                        if (monthlyTotal > 0) {
                                                            percentSaved = Math.round((1 - yearlyTotal / monthlyTotal) * 100);
                                                        }
                                                    }
                                                }
                                                return percentSaved && percentSaved > 0 ? (
                                                    <span className="badge bg-success ms-2 small">
                                                        {t('Saving')} {percentSaved}%
                                                    </span>
                                                ) : (
                                                    <span className="badge bg-success ms-2 small">
                                                        {t('Saving')}
                                                    </span>
                                                );
                                            })()}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Content */}
            <section className="mt-n8 pb-8">
                <Container>
                    <Row>
                        {(isLoading || (plans && plans.length === 0)) ? (
                            <>
                                {[1, 2, 3].map((_, idx) => (
                                    <Col lg={4} md={12} xs={12} key={`loading-${idx}`}>
                                        <Skeleton className="rounded" height="600px" count={1} />
                                    </Col>
                                ))}
                            </>
                        ) : (
                            getFilteredPlans().length > 0 && (
                                <>
                                    {getFilteredPlans().map((item, index) => {
                                        return (
                                            <Col lg={4} md={12} xs={12} key={index}>
                                                <PlanPricingCard content={item} />
                                            </Col>
                                        );
                                    })}
                                </>
                            )
                        )}
                    </Row>
                </Container>
            </section>
        </Fragment>

    );
};

export default PlanPricingTable;